"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AirwebTicketLegacyAccountService = void 0;
const exceptions_1 = require("../exceptions");
class AirwebTicketLegacyAccountService {
    constructor(client) {
        this.client = client;
    }
    async login(token, options = {}) {
        const payload = {
            firebase_token: token,
            network_id: options.networkId,
            user_id: options.userId,
            device: options.device,
            provider: options.provider,
            client: options.legacyClient,
            oauth_client: options.oauthClient,
            originAgent: options.originAgent,
            originType: options.originType,
        };
        if (options.withWallet !== undefined) {
            payload['withWallet'] = Boolean(options.withWallet) ? 1 : 0;
        }
        if (options.authAsGuest !== undefined) {
            payload['authAsGuest'] = Boolean(options.authAsGuest) ? 1 : 0;
        }
        if (options.forceProfiles !== undefined) {
            payload['forceProfiles'] = Boolean(options.forceProfiles) ? 1 : 0;
        }
        if (options.locale) {
            payload['locale'] = options.locale;
        }
        const { data } = await this.client.http.post('Account/login.json', payload, options);
        if (data.profiles) {
            throw new exceptions_1.AirwebTicketLegacyProfilesException(data.profiles);
        }
        if (!data.user) {
            throw new exceptions_1.AirwebTicketLegacyException('The response did not contain the user');
        }
        return data.user;
    }
    async getUser(userToken, userId, options = {}) {
        const body = { userToken, userId };
        if (options.networkId) {
            body.networkId = options.networkId;
        }
        const { data } = await this.client.http.post('Account/getUser.json', body, options);
        const user = data.user;
        if (!user.profiles && data.profiles)
            user.profiles = data.profiles;
        return user;
    }
    async update(userToken, userId, user, options = {}) {
        const body = Object.assign({ userToken,
            userId }, user);
        if (options.networkId) {
            body.networkId = options.networkId;
        }
        const { data } = await this.client.http.post('Account/update.json', body, options);
        return data.user;
    }
    async updatePicture(userToken, userId, picture, options = {}) {
        const body = new FormData();
        body.append('userToken', userToken);
        body.append('user_id', userId);
        body.append('media', picture);
        const { data } = await this.client.http.post('Media/uploadMedia.json', body, Object.assign(Object.assign({}, options), { headers: { 'Content-Type': 'multipart/form-data' } }));
        return data;
    }
    async getProfiles(userToken, options = {}) {
        const body = {
            userToken,
        };
        if (options.networkId) {
            body.networkId = options.networkId;
        }
        const { data } = await this.client.http.post('Account/getUser.json', body, options);
        return data.profiles;
    }
    async createProfile(userToken, profile, networkId = null, options = {}) {
        const { data } = await this.client.http.post('Account/createProfile.json', Object.assign({ userToken, network_id: networkId }, profile), options);
        return data;
    }
    async logout(userToken, userId, options = {}) {
        const { data } = await this.client.http.post('Account/logout.json', {
            user_id: userId,
            userToken,
        }, options);
        return data;
    }
    async getCards(userToken, networkId, methods = ['CARD'], options = {}) {
        const { data } = await this.client.http.get('Account/getCards.json', Object.assign(Object.assign({}, options), { params: {
                userToken,
                network_id: networkId,
                methods,
            } }));
        return data.CARDS;
    }
    async unregisterCard(userToken, cardId, options = {}) {
        const { data } = await this.client.http.post('Account/unregisterCard.json', {
            userToken,
            cardId,
        }, options);
        return data;
    }
    async introspectToken(userToken, options = {}) {
        if (options.useGet) {
            const { data } = await this.client.http.get('Account/introspectToken.json', Object.assign(Object.assign({}, options), { params: { userToken } }));
            return data;
        }
        const { data } = await this.client.http.post('Account/introspectToken.json', { userToken }, options);
        return data;
    }
    async getMedias(userToken, options = {}) {
        const { data } = await this.client.http.get('Account/getMedias.json', Object.assign(Object.assign({}, options), { params: Object.assign(Object.assign({}, (options.params || {})), { userId: options.userId, userToken }) }));
        return data;
    }
    async registerMedia(userToken, registerOptions, options = {}) {
        const { data } = await this.client.http.post('Account/registerMedia.json', Object.assign(Object.assign({}, registerOptions), { userToken, userId: options.userId }), options);
        return data;
    }
    async getWallet(userToken, walletOptions = {}, options = {}) {
        const parsedWalletOptions = {};
        if (walletOptions === null || walletOptions === void 0 ? void 0 : walletOptions.type) {
            parsedWalletOptions.type = walletOptions.type.toLowerCase();
        }
        if (walletOptions === null || walletOptions === void 0 ? void 0 : walletOptions.unstack) {
            parsedWalletOptions.unstack = walletOptions.unstack ? '1' : '0';
        }
        if (walletOptions === null || walletOptions === void 0 ? void 0 : walletOptions.userId) {
            parsedWalletOptions.userId = walletOptions.userId;
        }
        if (walletOptions === null || walletOptions === void 0 ? void 0 : walletOptions.networkId) {
            parsedWalletOptions.networkId = walletOptions.networkId;
        }
        if (walletOptions === null || walletOptions === void 0 ? void 0 : walletOptions.displayFullscreenVersion) {
            parsedWalletOptions.displayFullscreenVersion = walletOptions.displayFullscreenVersion;
        }
        if (walletOptions === null || walletOptions === void 0 ? void 0 : walletOptions.displayFullscreenDevicePublicId) {
            parsedWalletOptions.displayFullscreenDevicePublicId =
                walletOptions.displayFullscreenDevicePublicId;
        }
        if (walletOptions === null || walletOptions === void 0 ? void 0 : walletOptions.displayFullscreenDevicePublicKey) {
            parsedWalletOptions.displayFullscreenDevicePublicKey =
                walletOptions.displayFullscreenDevicePublicKey;
        }
        const { data } = await this.client.http.get('Account/getWallet', Object.assign(Object.assign({}, options), { params: Object.assign(Object.assign({ userToken }, parsedWalletOptions), options.params) }));
        return data.wallet;
    }
    async sendVerificationEmail(userToken, verificationOptions, options = {}) {
        const { data } = await this.client.http.post('Account/sendVerificationEmail', Object.assign({ userToken }, verificationOptions), options);
        return data;
    }
    async verify(code, options = {}) {
        const { data } = await this.client.http.put('Account/verify', { code }, options);
        return data;
    }
    async disableProfile(userToken, userId, options = {}) {
        const { data } = await this.client.http.put('Account/disableProfile', { userToken, userId }, options);
        return data;
    }
    async unregisterMedia(userToken, userId, mediaId, options = {}) {
        const { data } = await this.client.http.delete('Account/unregisterMedia', Object.assign(Object.assign({}, options), { params: {
                userToken,
                userId,
                mediaId,
            } }));
        return data;
    }
    async searchLocalMediaForSignin(mediaNumber, mediaCustomerBirthDate, networkId, options = {}) {
        const { data } = await this.client.http.get('Account/searchLocalMediaForSignin', Object.assign(Object.assign({}, options), { params: {
                mediaNumber,
                mediaCustomerBirthDate,
                networkId,
            } }));
        return data;
    }
    async signinWithToken(body, options = {}) {
        const { data } = await this.client.http.post('Account/signinWithToken', body, options);
        return data;
    }
    async resetPassword(email, networkId) {
        const { data } = await this.client.http.post('Account/resetPassword', {
            email,
            networkId,
        });
        return data;
    }
    async touch(userToken, options = {}) {
        const { data } = await this.client.http.post('Account/touch', {
            userToken,
        }, options);
        return data;
    }
    async setCardBookmark(cardId, userToken, userId, options = {}) {
        const { data } = await this.client.http.put('account/setCardBookmark', {
            cardId,
            userToken,
            userId,
        }, options);
        return data;
    }
    async removeCardBookmark(cardId, userToken, userId, options = {}) {
        const { data } = await this.client.http.put('account/removeCardBookmark', {
            cardId,
            userToken,
            userId,
        }, options);
        return data;
    }
}
exports.AirwebTicketLegacyAccountService = AirwebTicketLegacyAccountService;
//# sourceMappingURL=account.service.js.map